// ══════════════════════════════════════════════════════════════════════════
//  ACCESO · la pantalla: arranque, avisos al editar y rótulos (motor puro)
//
//  acceso.logic.js decide qué pasó y qué se le dice a la persona. Aquí vive lo
//  que la PANTALLA necesita decidir sin React:
//    - decidirArranque: con qué empieza la app cuando la URL trae un enlace de
//      Auth. Se decide ANTES de restaurar la sesión guardada: en una tablet con la
//      sesión de otro técnico, el enlace de recuperación abría su AppShell y nadie
//      leía el token.
//    - avisoTrasEditar: cuándo un aviso deja de ser cierto porque la persona
//      corrigió el campo del que hablaba.
//    - textoBoton, segundosHasta, partirPorCorreo e iconoDeAviso: lo que la vista
//      dibuja a partir del estado del hook (useAcceso).
//
//  Sin React y sin sb: se prueba con vitest (pantalla.logic.test.js).
// ══════════════════════════════════════════════════════════════════════════
import { RUTA_RESTABLECER, leerEnlaceAuth, urlSinParametrosAuth } from './acceso.logic.js';

// La ruta de una window.location o de una URL en texto (con o sin origen).
function rutaDe(ubicacion) {
  if (typeof ubicacion === 'string') {
    const sinOrigen = ubicacion.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/?#]*/i, '');
    const corte = sinOrigen.search(/[?#]/);
    return (corte >= 0 ? sinOrigen.slice(0, corte) : sinOrigen) || '/';
  }
  return (ubicacion && ubicacion.pathname) || '/';
}

// Lo que sigue a la ruta en una URL ya limpia: los parámetros y el hash ajenos a Auth.
function restoDe(url) {
  return url.slice(rutaDe(url).length);
}

/** ¿Es la ruta pública de contraseña nueva? Tolera la diagonal final. */
export function esRutaRestablecer(pathname) {
  return String(pathname || '').replace(/\/+$/, '') === RUTA_RESTABLECER;
}

/** El arranque de siempre: entrar y restaurar la sesión guardada. */
export const ARRANQUE_NORMAL = Object.freeze({
  restablecer: false,
  restaurarSesion: true,
  modoInicial: 'entrar',
  enlace: null,
  url: null,
});

/**
 * Cómo arranca la app según la URL. Se lee una vez, antes de restaurar la sesión.
 * @param {string|{pathname?:string, search?:string, hash?:string}} ubicacion  window.location o una URL
 * @returns {{restablecer:boolean, restaurarSesion:boolean, modoInicial:'entrar'|'recuperar'|'nueva'|'crear',
 *   enlace:Object|null, url:string|null}}
 *   - restablecer: la pantalla de contraseña nueva (o de crear, con una invitación), con un enlace
 *     útil o con el error que dejó Auth. Aplica en /restablecer y también en la raíz: los enlaces
 *     heredados y las invitaciones del panel caen en el Site URL.
 *   - restaurarSesion: false en /restablecer y con un enlace; la sesión guardada puede ser de otra
 *     persona y no se activa.
 *   - enlace: lo que devolvió leerEnlaceAuth, solo cuando hay pantalla de contraseña.
 *   - url: la dirección para history.replaceState (sin parámetros de Auth, en la ruta que
 *     corresponde), o null si no hay que tocarla.
 */
export function decidirArranque(ubicacion) {
  const enlace = leerEnlaceAuth(ubicacion);
  if (enlace.modo || enlace.error) {
    return {
      restablecer: true,
      restaurarSesion: false,
      modoInicial: enlace.modo || (enlace.tipo === 'invite' ? 'crear' : 'nueva'),
      enlace,
      url: `${RUTA_RESTABLECER}${restoDe(urlSinParametrosAuth(ubicacion))}`,
    };
  }
  if (esRutaRestablecer(rutaDe(ubicacion))) {
    // /restablecer sin enlace: se recargó con la URL ya limpia o se escribió a mano. No hay
    // token con qué guardar; lo que sí se puede es pedir otro enlace.
    return {
      restablecer: false,
      restaurarSesion: false,
      modoInicial: 'recuperar',
      enlace: null,
      url: `/${restoDe(urlSinParametrosAuth(ubicacion))}`,
    };
  }
  if (enlace.limpiar) return { ...ARRANQUE_NORMAL, url: urlSinParametrosAuth(ubicacion) };
  return ARRANQUE_NORMAL;
}

// ── Avisos ────────────────────────────────────────────────────────────────

// Avisos que hablan de LA CUENTA escrita: al cambiar el correo ya no son ciertos.
const CLASES_DE_LA_CUENTA = new Set([
  'acceso_desactivado', 'correo_sin_confirmar', 'sin_perfil', 'pendiente',
  'correo_no_autorizado', 'limite_correos', 'cuenta_existente',
]);

/**
 * El aviso que queda después de que la persona edita un campo.
 * @param {null|{clase:string, campo:null|string, tono:string}} aviso  de textoAcceso
 * @param {'correo'|'contrasena'|'confirmacion'|'actual'} campo  el campo editado
 * @returns el mismo aviso o null
 */
export function avisoTrasEditar(aviso, campo) {
  if (!aviso) return null;
  if (aviso.campo) {
    if (aviso.campo === campo) return null;
    // «Correo o contraseña incorrectos» habla de los dos campos.
    if (aviso.clase === 'credenciales' && (campo === 'correo' || campo === 'contrasena')) return null;
    // Cambiar la primera cambia la comparación.
    if (aviso.clase === 'no_coinciden' && campo === 'contrasena') return null;
    return aviso;
  }
  if (aviso.tono === 'error' && campo === 'correo' && CLASES_DE_LA_CUENTA.has(aviso.clase)) return null;
  return aviso;
}

// Un glifo por lo que el aviso dice (DESIGN.md: el icono refleja la función).
const ICONO_POR_CLASE = {
  sesion_cerrada: 'lock',
  por_seguridad: 'lock',
  reautenticacion: 'lock',
  acceso_desactivado: 'user',
  sin_perfil: 'user',
  cuenta_existente: 'user',
  pendiente: 'clock',
  limite_intentos: 'clock',
  limite_correos: 'clock',
  correo_sin_confirmar: 'mail',
  correo_no_autorizado: 'mail',
  enlace_vencido: 'link',
  sin_conexion: 'cloud-off',
  contrasena_guardada: 'check-circle',
  permiso_solicitud: 'info',
};

/** Icono (nombre de Icon) de un aviso de cuenta o de red. */
export function iconoDeAviso(aviso) {
  if (!aviso) return null;
  // Con el token del enlace, una sesión inválida es un enlace que ya no sirve.
  if (aviso.accion && aviso.accion.id === 'pedir_enlace') return 'link';
  return ICONO_POR_CLASE[aviso.clase] || 'alert-circle';
}

// ── Rótulos ───────────────────────────────────────────────────────────────

/** Segundos enteros que faltan para `hasta` (ms). 0 = ya. */
export function segundosHasta(hasta, ahora) {
  const h = Number(hasta) || 0;
  return h > ahora ? Math.ceil((h - ahora) / 1000) : 0;
}

/** El rótulo del botón del formulario. La espera del bloqueo local se cuenta en el propio botón. */
export function textoBoton({ modo, enviando = false, esperaSeg = 0 } = {}) {
  if (modo === 'recuperar') return enviando ? 'Enviando…' : 'Enviar enlace';
  if (modo === 'nueva' || modo === 'crear' || modo === 'cambiar') return enviando ? 'Guardando…' : 'Guardar contraseña';
  if (enviando) return 'Verificando…';
  if (esperaSeg > 0) return `Espera ${esperaSeg} s`;
  return 'Entrar';
}

/** Parte un texto alrededor del correo para dibujarlo con peso. Sin el correo, todo va en `antes`. */
export function partirPorCorreo(texto, correo) {
  const t = String(texto == null ? '' : texto);
  const c = String(correo == null ? '' : correo);
  const i = c ? t.indexOf(c) : -1;
  if (i < 0) return { antes: t, correo: '', despues: '' };
  return { antes: t.slice(0, i), correo: c, despues: t.slice(i + c.length) };
}
